import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Section = ({ title, children }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <h3 className="t-h3" style={{ marginBottom: 20 }}>{title}</h3>
    {children}
  </div>
);

const ToggleRow = ({ label, description, value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
    <div>
      <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13.5, color: '#1e1b4b' }}>{label}</div>
      {description && <div className="t-meta" style={{ marginTop: 2 }}>{description}</div>}
    </div>
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 40, height: 22, borderRadius: 11, flexShrink: 0,
        background: value ? '#4f46e5' : '#d1d5db',
        position: 'relative', transition: 'background .2s', cursor: 'pointer',
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: value ? 20 : 3,
        width: 16, height: 16, borderRadius: '50%',
        background: '#fff', transition: 'left .2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  </div>
);

const Toast = ({ message, visible }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    background: '#eaf3de', color: '#3b6d11', border: '0.5px solid #97c459',
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.2s, transform 0.2s', pointerEvents: 'none',
  }}>
    {message}
  </div>
);

const Settings = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [name,  setName]  = useState(user?.name  || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  const [prefs, setPrefs] = useState({
    emailNotifications:   true,
    smsNotifications:     true,
    appointmentReminders: true,
    prescriptionUpdates:  true,
    promotionalEmails:    false,
  });

  const togglePref = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const showToast = () => {
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2500);
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await new Promise(r => setTimeout(r, 400));
    updateUser?.({ name: name.trim(), email: email.trim() });
    setSaving(false);
    showToast();
  };

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Settings</span>
        </div>

        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          <h1 className="t-h1" style={{ marginBottom: 4 }}>⚙️ Settings</h1>
          <p className="t-body-sm">Manage your profile and notification preferences</p>
        </div>

        {/* Profile */}
        <Section title="Profile">
          <div className="grid-2col" style={{ marginBottom: 16 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Full name</label>
              <input
                className="input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your full name"
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Email</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label className="t-label">Phone</label>
            <input
              className="input"
              value={user?.phone || ''}
              disabled
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            />
            <div className="t-meta" style={{ marginTop: 4 }}>Phone number cannot be changed.</div>
          </div>
          <button
            className="btn-primary"
            style={{ maxWidth: 200 }}
            disabled={saving || !name.trim()}
            onClick={handleSaveProfile}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </Section>

        {/* Notifications */}
        <Section title="Notifications">
          <ToggleRow
            label="Email notifications"
            description="Receive updates and confirmations by email"
            value={prefs.emailNotifications}
            onChange={() => togglePref('emailNotifications')}
          />
          <ToggleRow
            label="SMS notifications"
            description="Get appointment reminders via SMS"
            value={prefs.smsNotifications}
            onChange={() => togglePref('smsNotifications')}
          />
          <ToggleRow
            label="Appointment reminders"
            description="Reminders 24h and 1h before your appointment"
            value={prefs.appointmentReminders}
            onChange={() => togglePref('appointmentReminders')}
          />
          <ToggleRow
            label="Prescription updates"
            description="Notifications when your prescription status changes"
            value={prefs.prescriptionUpdates}
            onChange={() => togglePref('prescriptionUpdates')}
          />
          <ToggleRow
            label="Promotional emails"
            description="Health tips and offers from our partners"
            value={prefs.promotionalEmails}
            onChange={() => togglePref('promotionalEmails')}
          />
        </Section>

        {/* Account */}
        <Section title="Account">
          <div style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2 }}>
              Role
            </div>
            <div className="t-meta">{user?.role || '—'}</div>
          </div>
          <div style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2 }}>
              Member since
            </div>
            <div className="t-meta">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
            </div>
          </div>
          <div style={{ padding: '10px 0' }}>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13.5, color: '#dc2626', marginBottom: 6 }}>
              Danger zone
            </div>
            <button
              style={{
                background: 'none', border: '1px solid #fca5a5', borderRadius: 9,
                padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#dc2626',
                cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'background .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              Delete account
            </button>
          </div>
        </Section>

      </main>
      <Toast message="Settings saved" visible={toastVisible} />
    </div>
  );
};

export default Settings;